// ─────────────────────────────────────────────
//  sheetEngine — the ON/OFF control (and liveness) for the Google Sheet
//  live-sync engine.
//
//  WHAT THE ENGINE IS: an out-of-band process (Python, its own service
//  account) that watches the daily Google Sheet and pushes changes into the
//  live board between the nightly imports. It runs OUTSIDE this app, so the
//  app can neither start it nor stop it — all it can do is publish a flag the
//  engine agrees to obey, and show whether the engine is honouring it.
//
//  ⚠️ WHY THIS IS A FIRESTORE FIELD AND NOT AN HTTP ENDPOINT.
//  The original spec asked for `GET /api/settings/google-sheet-engine-status`.
//  ATC has no REST API server — it is React + Firestore + Cloud Functions v2 —
//  so that endpoint has nowhere to live, and standing one up would put a NEW
//  internet-facing surface in front of the switch that gates writes to a real
//  hospital's OR board. The engine already authenticates to Firestore with its
//  own service account for the data it syncs; reading one more field on a doc
//  it can already read costs nothing and adds no attack surface. So the
//  contract is a document field, and THIS MODULE IS THAT CONTRACT — the Setup
//  panel, the security rules and the Python engine all have to mean the same
//  thing by "ON", and they only do if they read it from one description.
//
//  ── THE CONTRACT (this is what the Python engine implements) ─────────────
//
//  DOCUMENT: the same facility doc the global sheet-import settings already
//  live on — `facilities/{DEFAULT_FACILITY_ID}`, i.e. `facilities/default`
//  unless REACT_APP_DEFAULT_FACILITY_ID overrides it (see src/config/roles.js
//  and functions/dailyImport.js, which reads globalSheetImportConfig from the
//  very same doc). The engine is global, not per-campus: one sheet, one
//  process, one switch.
//
//  FIELD 1 — `sheetEngineEnabled` (boolean). The switch. Written by an ADMIN
//  from Setup → Facility → Google Sheet Import. The engine polls it and does
//  no sync work while it is anything other than boolean `true`.
//
//  FIELD 2 — `sheetEngineHeartbeat` (map). The proof of life. Written by the
//  ENGINE, never by the app. Shape:
//      {
//        at:      <Firestore Timestamp>   REQUIRED — the moment of check-in
//        status:  "ok" | "error"          optional, absent means "ok"
//        message: <string, <=200 chars>   optional, shown when status is error
//      }
//  Write it at least once every SHEET_ENGINE_HEARTBEAT_INTERVAL_MS while the
//  process is alive — INCLUDING while the switch is OFF, because "OFF and the
//  engine is still up" and "OFF and the engine is gone" are different facts
//  and the panel shows both.
//
//  ⚠️ WHY TWO TOP-LEVEL FIELDS AND NOT ONE `sheetEngine` MAP. It reads worse
//  and it is not a style choice: Firestore rules' `diff().affectedKeys()`
//  works on the document's TOP-LEVEL keys only. Nested inside one map, "an
//  admin may write the switch" and "no client may forge the heartbeat" are
//  the same key to the rules engine and cannot be separated. Two keys is what
//  makes the rule expressible at all. See firestore.rules → FACILITIES.
//
//  ── FAIL-CLOSED, DELIBERATELY ────────────────────────────────────────────
//  Everything below reads anything that is not exactly boolean `true` as OFF:
//  a missing field, a fresh facility doc, `"true"`, `1`, a half-written map.
//  The failure this prevents is asymmetric. Reading a malformed flag as OFF
//  costs an admin one click. Reading it as ON lets an unattended process
//  rewrite a live OR board from a spreadsheet nobody has approved — and the
//  board would look completely normal while it happened.
//
//  Liveness fails closed the same way: an unparseable or absent `at` is NOT
//  "recent", it is "never checked in". A toggle that reads ON beside a dead
//  engine is a lie the owner would act on — they would stop chasing the sheet
//  because the app told them it was being synced.
//
//  Pure. No React, no Firebase, no clock of its own — `now` is always passed
//  in so the states are provable at any hour (a test whose fixture times are
//  relative to the real clock is green only at the hour it was written).
// ─────────────────────────────────────────────

// ── Field names ──────────────────────────────────────────────────────────
// Exported rather than typed as literals at each call site so the panel, the
// tests and any future reader (a Cloud Function, an ops script) cannot drift
// apart from the engine. The rules file cannot import these — it is not
// JavaScript — so the two string literals there carry a pointer back here.
export const SHEET_ENGINE_ENABLED_FIELD = "sheetEngineEnabled";
export const SHEET_ENGINE_HEARTBEAT_FIELD = "sheetEngineHeartbeat";

// How often the engine promises to check in while it is alive.
export const SHEET_ENGINE_HEARTBEAT_INTERVAL_MS = 60 * 1000;

// How long a silence has to last before the panel stops believing the engine
// is running. Five minutes = five missed check-ins, which is long enough that
// a slow poll, a redeploy or a clock skew of a few seconds does not cry wolf,
// and short enough that a dead engine is caught inside one OR turnover.
export const SHEET_ENGINE_STALE_AFTER_MS = 5 * 60 * 1000;

// The composite states the panel can be in. Exported so tests assert on an id
// rather than on prose — the wording of a label is allowed to change, the
// meaning is not.
export const SHEET_ENGINE_STATUS = {
  OFF: "off",           // switch OFF, engine quiet — the normal stopped state
  STOPPING: "stopping", // switch OFF, but the engine is still checking in
  STARTING: "starting", // switch ON, engine has never checked in at all
  STALLED: "stalled",   // switch ON, engine has gone silent — the dangerous one
  ERRORING: "erroring", // switch ON, engine alive but reporting its own failure
  RUNNING: "running",   // switch ON, engine checked in within the window
};

/**
 * Is the engine switched ON?
 *
 * @param {object|null|undefined} facilityDoc — the facilities/default document
 * @returns {boolean} true ONLY for a stored boolean true.
 */
export function sheetEngineEnabled(facilityDoc) {
  // `=== true` and nothing looser. `Boolean(x)` would turn the string "false"
  // — which is what a hand-edited doc or a shell script's `--set enabled=false`
  // leaves behind — into ON, i.e. exactly backwards.
  return facilityDoc?.[SHEET_ENGINE_ENABLED_FIELD] === true;
}

/**
 * Normalise whatever is stored in `sheetEngineHeartbeat.at` to epoch ms.
 *
 * The value's runtime type depends on who wrote it and how it got here:
 *  - a Firestore Timestamp from the client SDK (has .toMillis())
 *  - a Timestamp from the Admin SDK / a rehydrated one (has .seconds)
 *  - a Date, if a test or a Function passed one straight through
 *  - epoch milliseconds, if the engine wrote a plain number
 *  - an ISO-8601 string, if the engine wrote text
 * All five are accepted because the alternative is a heartbeat that is
 * silently unreadable — which renders identically to a dead engine and would
 * send someone hunting a process that is running perfectly well.
 *
 * Anything else — null, {}, "soon", NaN, a Timestamp whose seconds is a
 * string — is `null`, which every caller treats as "never checked in".
 *
 * @param {object|null|undefined} facilityDoc
 * @returns {number|null} epoch milliseconds, or null if there is no usable one
 */
export function sheetEngineHeartbeatAt(facilityDoc) {
  const hb = facilityDoc?.[SHEET_ENGINE_HEARTBEAT_FIELD];
  if (!hb || typeof hb !== "object") return null;
  const at = hb.at;
  if (at == null) return null;

  let ms = null;
  if (typeof at.toMillis === "function") {
    // Firestore Timestamp. Wrapped: a Timestamp built from junk throws here
    // rather than returning a number, and one bad write must not blank the
    // whole panel.
    try { ms = at.toMillis(); } catch { return null; }
  } else if (typeof at.seconds === "number") {
    ms = at.seconds * 1000 + Math.floor((at.nanoseconds || 0) / 1e6);
  } else if (at instanceof Date) {
    ms = at.getTime();
  } else if (typeof at === "number") {
    ms = at;
  } else if (typeof at === "string") {
    ms = Date.parse(at);
  }

  // Date.parse of nonsense is NaN; a Date built from nonsense is NaN too.
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Self-reported status from the last check-in: "ok" | "error".
 * Absent or unrecognised reads as "ok" — the engine checking in AT ALL is the
 * signal this field decorates, and an unknown status string must not be
 * escalated into an alarm the operator cannot act on.
 */
function heartbeatStatus(facilityDoc) {
  const hb = facilityDoc?.[SHEET_ENGINE_HEARTBEAT_FIELD];
  return hb && hb.status === "error" ? "error" : "ok";
}

/** The engine's own short note about a failure, capped for display. */
function heartbeatMessage(facilityDoc) {
  const hb = facilityDoc?.[SHEET_ENGINE_HEARTBEAT_FIELD];
  const msg = hb && typeof hb.message === "string" ? hb.message.trim() : "";
  return msg ? msg.slice(0, 200) : null;
}

/**
 * "just now" / "3m ago" / "2h ago" — a human age for a check-in.
 *
 * Rounds DOWN and never says "0s ago", because the point of this string is to
 * be read aloud across a desk, not to be precise. A negative age (the engine's
 * clock is ahead of the browser's) is reported as "just now" rather than
 * "in 4s" — clock skew is not information the operator can use.
 *
 * @param {number|null} ageMs
 * @returns {string|null} null when there is no age to describe
 */
export function sheetEngineAgeLabel(ageMs) {
  if (!Number.isFinite(ageMs)) return null;
  if (ageMs < 10 * 1000) return "just now";
  if (ageMs < 60 * 1000) return `${Math.floor(ageMs / 1000)}s ago`;
  if (ageMs < 60 * 60 * 1000) return `${Math.floor(ageMs / 60000)}m ago`;
  if (ageMs < 24 * 60 * 60 * 1000) return `${Math.floor(ageMs / 3600000)}h ago`;
  return `${Math.floor(ageMs / 86400000)}d ago`;
}

/**
 * The single reader every surface uses. Combines the switch and the liveness
 * into one composite state, so no screen has to re-derive "ON but dead".
 *
 * @param {object|null|undefined} facilityDoc — facilities/default, or null
 * @param {object} [opts]
 *   @param {number} [opts.now] — epoch ms; defaults to Date.now(). ALWAYS pass
 *     it in tests: fixtures relative to the real clock pass at the hour they
 *     were written and fail at midnight.
 *   @param {number} [opts.staleAfterMs] — override the silence window.
 * @returns {{
 *   enabled: boolean,
 *   status: string,          // one of SHEET_ENGINE_STATUS
 *   tone: string,            // "neutral" | "ok" | "warn" | "error"
 *   live: boolean,           // checked in inside the window
 *   heartbeatAtMs: number|null,
 *   ageMs: number|null,
 *   ageLabel: string|null,
 *   label: string,           // the short state, e.g. "ON — running"
 *   detail: string,          // one sentence saying what to do about it
 *   message: string|null,    // the engine's own error note, if any
 * }}
 */
export function sheetEngineState(facilityDoc, opts = {}) {
  const now = Number.isFinite(opts.now) ? opts.now : Date.now();
  const staleAfterMs = Number.isFinite(opts.staleAfterMs)
    ? opts.staleAfterMs
    : SHEET_ENGINE_STALE_AFTER_MS;

  const enabled = sheetEngineEnabled(facilityDoc);
  const heartbeatAtMs = sheetEngineHeartbeatAt(facilityDoc);
  const ageMs = heartbeatAtMs == null ? null : now - heartbeatAtMs;

  // A check-in from the FUTURE (engine clock ahead of the browser) still
  // counts as live — it is skew, not silence. Only elapsed silence is stale.
  const live = ageMs != null && ageMs < staleAfterMs;
  const errored = heartbeatStatus(facilityDoc) === "error";
  const message = heartbeatMessage(facilityDoc);
  const ageLabel = sheetEngineAgeLabel(ageMs);

  let status;
  let tone;
  let label;
  let detail;

  if (!enabled) {
    if (live) {
      // The switch is off but the process has not noticed yet — expected for
      // up to one poll interval after a flip, and a genuine finding after
      // that (an engine that ignores the switch is an engine writing to a
      // board nobody has authorised).
      status = SHEET_ENGINE_STATUS.STOPPING;
      tone = "warn";
      label = "OFF — engine still checking in";
      detail = `The switch is off, but the engine checked in ${ageLabel}. It should stop within a minute; if it keeps checking in, it is not reading the switch.`;
    } else {
      status = SHEET_ENGINE_STATUS.OFF;
      tone = "neutral";
      label = "OFF";
      detail = "The sheet is not being synced. The nightly import still runs.";
    }
  } else if (heartbeatAtMs == null) {
    // ON, and nothing has EVER checked in. Distinct from stalled: this is the
    // normal state for the first seconds after a flip, and a hard finding if
    // it persists. Deliberately "warn" rather than "error" — a red alarm on
    // every legitimate switch-on teaches people to ignore red, and then the
    // stalled state below stops being read.
    status = SHEET_ENGINE_STATUS.STARTING;
    tone = "warn";
    label = "ON — waiting for the engine";
    detail = "The switch is on but the engine has never checked in. If this does not clear within a minute, the engine is not running.";
  } else if (!live) {
    // THE STATE THIS PANEL EXISTS FOR. Switched on, nothing checked in for the
    // whole window: the board is NOT being synced and, without this line, the
    // toggle alone would say it was.
    status = SHEET_ENGINE_STATUS.STALLED;
    tone = "error";
    label = "ON — but the engine is not running";
    detail = `Last check-in ${ageLabel}. The sheet is NOT being synced right now. Restart the engine, or switch it off so nobody relies on it.`;
  } else if (errored) {
    status = SHEET_ENGINE_STATUS.ERRORING;
    tone = "error";
    label = "ON — engine reporting an error";
    detail = `The engine checked in ${ageLabel} but reported a failure. The sheet may not be syncing.`;
  } else {
    status = SHEET_ENGINE_STATUS.RUNNING;
    tone = "ok";
    label = "ON — running";
    detail = `Syncing. Last check-in ${ageLabel}.`;
  }

  return {
    enabled,
    status,
    tone,
    live,
    heartbeatAtMs,
    ageMs,
    ageLabel,
    label,
    detail,
    message,
  };
}
